import { useEffect, useState } from "react";
import { Box, Card, CardContent, Grid, Typography } from "@mui/material";
import QrCodeIcon from "@mui/icons-material/QrCode";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import Loader from "shared/components/loader/animation";
import { toastMessage } from "shared/components/toast";
import { getQRCodeStatisticsService } from "shared/services/qrCodeService";
import useBreadcrumbs from "shared/customHooks/useBreadCrumbs";
import { routeConstant } from "shared/routes/routesConstant";
import styles from "./style.module.scss";

interface StatisticsData {
  total: number;
  active: number;
  inactive: number;
  used: number;
  unused: number;
  expired: number;
}

function QRCodeOverview() {
  const [loading, setLoading] = useState<boolean>(false);
  const [statistics, setStatistics] = useState<StatisticsData>({
    total: 0,
    active: 0,
    inactive: 0,
    used: 0,
    unused: 0,
    expired: 0,
  });

  useBreadcrumbs({
    title: "QR Code Overview",
    path: routeConstant.qrCodeOverview.path,
  });

  useEffect(() => {
    fetchStatistics();
  }, []);

  const fetchStatistics = () => {
    setLoading(true);
    getQRCodeStatisticsService()
      .then(({ data }) => {
        if (data?.success || data?.status) {
          setStatistics(data?.data || {});
        }
        setLoading(false);
      })
      .catch((err) => {
        console.log("err", err?.response?.data?.message);
        toastMessage("error", err?.response?.data?.message || "Failed to fetch statistics");
        setLoading(false);
      });
  };

  const statsCards = [
    {
      title: "Total QR Codes",
      value: statistics.total,
      icon: <QrCodeIcon className={styles.icon} />,
      color: "#4A90E2",
      bgColor: "#E3F2FD",
    },
    {
      title: "Active",
      value: statistics.active,
      icon: <CheckCircleIcon className={styles.icon} />,
      color: "#4CAF50",
      bgColor: "#E8F5E9",
    },
    {
      title: "Inactive",
      value: statistics.inactive,
      icon: <CancelIcon className={styles.icon} />,
      color: "#F44336",
      bgColor: "#FFEBEE",
    },
    {
      title: "Used",
      value: statistics.used,
      icon: <AssignmentTurnedInIcon className={styles.icon} />,
      color: "#9C27B0",
      bgColor: "#F3E5F5",
    },
    {
      title: "Unused",
      value: statistics.unused,
      icon: <PendingActionsIcon className={styles.icon} />,
      color: "#FF9800",
      bgColor: "#FFF3E0",
    },
    {
      title: "Expired",
      value: statistics.expired,
      icon: <EventAvailableIcon className={styles.icon} />,
      color: "#607D8B",
      bgColor: "#ECEFF1",
    },
  ];

  if (loading) {
    return <Loader />;
  }

  return (
    <Box className={styles.container}>
      <Box className={styles.header}>
        <Typography variant="h5" className={styles.title}>
          QR Code Statistics Overview
        </Typography>
        <Typography variant="body2" className={styles.subtitle}>
          Monitor your subscription QR codes performance
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {statsCards.map((card, index) => (
          <Grid item xs={12} sm={6} md={4} lg={2} key={index}>
            <Card
              className={styles.statCard}
              sx={{
                borderLeft: `4px solid ${card.color}`,
              }}
            >
              <CardContent className={styles.cardContent}>
                <Box className={styles.cardHeader}>
                  <Box
                    className={styles.iconWrapper}
                    sx={{
                      backgroundColor: card.bgColor,
                      color: card.color,
                    }}
                  >
                    {card.icon}
                  </Box>
                </Box>
                <Typography variant="h4" className={styles.value} sx={{ color: card.color }}>
                  {card.value.toLocaleString()}
                </Typography>
                <Typography variant="body2" className={styles.cardTitle}>
                  {card.title}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Box className={styles.infoSection} mt={4}>
        <Card className={styles.infoCard}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              About QR Code Management
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              This overview provides a snapshot of your subscription QR codes. You can track the total number of codes generated, their status (active/inactive), usage statistics, and expired codes.
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Navigate to <strong>QR Management</strong> to generate new codes, manage existing ones, and download QR code images.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}

export default QRCodeOverview;
